import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { message, Modal, Space, Tag, theme, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { AssignmentStatus, RetrieveAssignmentSchema } from '@app/models/users';
import { DropDownMenuButton } from '@components/DropDownMenuButton';

import { useRevokeAssignmentMutation } from '@pages/Employees/hooks';

import { VisibilityChips } from './components/VisibilityChips';
import { formatTerm } from './services';
import { AssignmentBlock, BlockDivider, StatusTag } from './styles';

interface IProps {
  assignment: RetrieveAssignmentSchema;
  roleName: string;
  canManage: boolean;
  onEdit: (assignment: RetrieveAssignmentSchema) => void;
}

/** Блок одного назначения. */
export const AssignmentItem: React.FC<IProps> = ({ assignment, roleName, canManage, onEdit }) => {
  const assignmentsT = useTranslation(NSKeys.assignments);
  const commonT = useTranslation(NSKeys.common);
  const { token } = theme.useToken();
  const [modal, contextHolder] = Modal.useModal();
  const revoke = useRevokeAssignmentMutation();
  const status = assignment.status;
  // Базовое назначение неизменяемо. Отсутствующее серверное разрешение трактуем как запрет.
  const showActions = canManage && status !== AssignmentStatus.Revoked && !assignment.is_baseline;
  const canUpdate = assignment.$permissions?.update === true;
  const canRevoke = assignment.$permissions?.revoke === true;

  const STATUS_VIEW: Record<AssignmentStatus, { color: string; title: string }> = {
    [AssignmentStatus.Active]: { color: token.colorSuccess, title: assignmentsT.t('status.active') },
    [AssignmentStatus.Scheduled]: { color: token.colorInfo, title: assignmentsT.t('status.scheduled') },
    [AssignmentStatus.Expired]: { color: token.colorTextDisabled, title: assignmentsT.t('status.expired') },
    [AssignmentStatus.Revoked]: { color: token.colorError, title: assignmentsT.t('status.revoked') },
  };

  const handleRevoke = () => {
    modal.confirm({
      title: assignmentsT.t('revoke.confirmTitle'),
      centered: true,
      icon: <ExclamationCircleOutlined />,
      content: assignmentsT.t('revoke.confirmText'),
      okText: assignmentsT.t('revoke.confirmOk'),
      okButtonProps: { danger: true, loading: revoke.isLoading },
      cancelText: commonT.t('buttonCancel'),
      onOk: async () => {
        try {
          await revoke.mutateAsync(assignment.uid);
          message.success(assignmentsT.t('revoke.success'));
        } catch {
          message.error(commonT.t('error400'));
        }
      },
    });
  };

  return (
    <AssignmentBlock>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <Space size={8} wrap align="center">
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {roleName}
            </Typography.Title>
            {assignment.is_baseline && <Tag color="blue">{assignmentsT.t('status.baseline')}</Tag>}
            <Tooltip
              placement="top"
              title={`${assignmentsT.t('term.label')}: ${formatTerm(
                assignment.valid_from,
                assignment.valid_to,
                assignmentsT.t
              )}`}>
              <StatusTag $color={STATUS_VIEW[status].color}>{STATUS_VIEW[status].title}</StatusTag>
            </Tooltip>
          </Space>
        </div>
        <Space size={8} wrap style={{ justifyContent: 'flex-end' }}>
          {showActions && (
            <DropDownMenuButton
              type="link"
              placement="bottomRight"
              menu={[
                ...(canUpdate
                  ? [
                      {
                        label: <Typography.Text>{assignmentsT.t('actions.edit')}</Typography.Text>,
                        key: 'editAssignment',
                        onClick: () => onEdit(assignment),
                      },
                    ]
                  : []),
                ...(canRevoke
                  ? [
                      {
                        label: <Typography.Text>{assignmentsT.t('actions.revoke')}</Typography.Text>,
                        key: 'revokeAssignment',
                        onClick: handleRevoke,
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </Space>
      </div>
      {contextHolder}
      {assignment.visibility_rule && assignment.visibility_rule.conditions?.length ? (
        <>
          <BlockDivider titlePlacement="left" size="small">
            <Typography.Text type="secondary">{assignmentsT.t('visibility.sectionOrders')}</Typography.Text>
          </BlockDivider>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <VisibilityChips rule={assignment.visibility_rule} />
          </div>
        </>
      ) : (
        <>
          <BlockDivider size="small" />
          <Typography.Text type="secondary" italic>
            {assignmentsT.t('visibility.none')}
          </Typography.Text>
        </>
      )}
    </AssignmentBlock>
  );
};
